import {
  EventStatus,
  EventType,
  Network,
  RecordWithPlaintext,
  useEvent,
  useEvents,
  useRequestCreateEvent,
} from '@puzzlehq/sdk';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { Route as GameIndexRoute } from '@src/routes/game/index.js';
import { Route as MintRoute } from '@src/routes/game/mint.js';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  asciiToU128,
  u128ToAscii,
  useFeeQuery,
} from '@puzzlehq/frontend-utils';
import crypto from 'crypto';
import toast from 'react-hot-toast';
import { useSquashGameState } from '@src/providers/squashGameState';
import debug from 'debug';
import { useQuery } from '@tanstack/react-query';
import { extractAmountFromInputString } from 'jigsaw';

dayjs.extend(duration);

const network: Network = import.meta.env.VITE_NETWORK!;
const programId: string = import.meta.env.VITE_SQUASH_PROGRAM_ID!;

export const log_water = debug('squash:water');
log_water.enabled = true;

export const log_mint = debug('squash:mint');
log_mint.enabled = true;

function getRandomU32() {
  const buffer = crypto.randomBytes(4);
  return buffer.readUInt32BE(0);
}

export const useSquash = () => {
  const { activeSquashRecords, activeSquash, activeSquashRecord, loading } =
    useSquashGameState();

  const { endTimeUnix } = useSquashTime();

  const [timeRemaining, setTimeRemaining] = useState<undefined | number>(
    undefined
  );
  const [timeRemainingStr, setTimeRemainingStr] = useState<undefined | string>(
    undefined
  );

  useEffect(() => {
    if (endTimeUnix) {
      const updateRemainingTime = () => {
        const now = Date.now();
        const endTime = endTimeUnix * 1000;
        const remainingTime = endTime - now;

        if (remainingTime > 0) {
          setTimeRemaining(remainingTime);
          const duration = dayjs.duration(remainingTime);
          const days = duration.days();
          const hours = duration.hours();
          const minutes = duration.minutes();
          const seconds = duration.seconds();
          const formattedTime = `${days >= 1 ? `${days}d ` : ''}${
            hours >= 1 ? `${hours}h ` : ''
          }${minutes >= 1 ? `${minutes}m ` : ''}${seconds}s`;

          setTimeRemainingStr(formattedTime);
        } else {
          setTimeRemaining(0);
          setTimeRemainingStr('0s');
        }
      };

      updateRemainingTime(); // Initial call to set the time remaining
      const intervalId = setInterval(updateRemainingTime, 1000); // Update every second

      return () => clearInterval(intervalId);
    }
  }, [endTimeUnix]);

  return {
    activeSquashRecords,
    loading,
    activeSquash,
    activeSquashRecord,
    timeRemaining,
    timeRemainingStr,
  };
};

export const useSquashWater = () => {
  const {
    expectedRecordCount: _expectedRecordCount,
    waterEventId: waterEventId_a,
  } = useSearch({
    from: GameIndexRoute.id,
  });
  const { events } = useEvents({
    network,
    filter: {
      programId,
    },
  });

  const waterEventId_b = useMemo(() => {
    const event = events
      ?.filter((e) =>
        ['water', 'water_and_level_up'].includes(e.functionId ?? '')
      )
      .find((e) =>
        [EventStatus.Creating, EventStatus.Pending].includes(e.status)
      );
    return event?._id;
  }, [JSON.stringify(events)]);

  const waterEventId = waterEventId_a ?? waterEventId_b;

  const { activeSquashRecord, activeSquash, activeSquashRecords } = useSquash();
  const { gameOver, setGameOver } = useSquashGameState();

  // Cache the previous level in a ref
  const prevLevelRef = useRef(activeSquash?.onChainLevel ?? 0);
  useEffect(() => {
    if (activeSquash?.onChainLevel !== undefined) {
      prevLevelRef.current = activeSquash.onChainLevel;
    }
  }, [activeSquash?.onChainLevel]);

  const currentRecordCount = activeSquashRecords?.length;

  const [expectedRecordCount, setExpectedRecordCount] =
    useState(_expectedRecordCount);

  useEffect(() => {
    if (currentRecordCount !== undefined && expectedRecordCount === undefined) {
      setExpectedRecordCount(currentRecordCount + 1);
    }
  }, [currentRecordCount, expectedRecordCount]);

  const navigate = GameIndexRoute.useNavigate();

  const { gameIsActive, dayStartUnix, timeDeltaSeconds, endTimeUnix } =
    useSquashTime();
  const [isWatering, setIsWatering] = useState(false);

  const wateredYesterday =
    Date.now() / 1000 - (activeSquash?.lastWatered.getTime() ?? 0) / 1000 <
    (timeDeltaSeconds ?? 0) * 2;
  const readyToLevelUp =
    wateredYesterday &&
    activeSquash &&
    (activeSquash.kg + 1) / 10 >= activeSquash.onChainLevel + 1;

  const functionId = readyToLevelUp ? 'water_and_level_up' : 'water';

  const feeQuery = useFeeQuery({
    programId,
    functionId,
    network,
  });
  const fee = feeQuery.data?.fee?.fee.total;

  const needsWater =
    !!activeSquash && ['sad', 'thirsty'].includes(activeSquash.mood);
  const readyToWater =
    gameIsActive &&
    !!activeSquashRecord?.plaintext &&
    !!dayStartUnix &&
    !!timeDeltaSeconds &&
    !!activeSquashRecords &&
    !!endTimeUnix &&
    !!fee &&
    endTimeUnix > Date.now() / 1000;

  const {
    createEvent,
    error,
    eventId: _eventId,
    loading,
    settlementStatus,
  } = useRequestCreateEvent({
    programId,
    fee: fee!,
    functionId: functionId,
    inputs: readyToWater
      ? [
          activeSquashRecord.plaintext,
          `${dayStartUnix}u64`,
          `${Math.floor(Date.now() / 1000)}u64`,
          `${timeDeltaSeconds}u64`,
        ]
      : [],
    type: readyToLevelUp ? EventType.SquashLevelUp : EventType.SquashWater,
    settlementInfo:
      currentRecordCount !== undefined && expectedRecordCount !== undefined
        ? {
            currentRecordCount,
            expectedRecordCount,
            eventId: waterEventId,
          }
        : undefined,
    tokenIds: ['4255871388field'],
  });

  useEffect(() => {
    readyToLevelUp && log_water('readyToLevelUp', readyToLevelUp);
  }, [readyToLevelUp]);

  useEffect(() => {
    settlementStatus && log_water('settlementStatus', settlementStatus);
  }, [settlementStatus]);

  useEffect(() => {
    currentRecordCount && log_water('currentRecordCount', currentRecordCount);
  }, [currentRecordCount]);

  useEffect(() => {
    _expectedRecordCount &&
      log_water('_expectedRecordCount', _expectedRecordCount);
  }, [_expectedRecordCount]);

  useEffect(() => {
    expectedRecordCount &&
      log_water('expectedRecordCount', expectedRecordCount);
  }, [expectedRecordCount]);

  useEffect(() => {
    log_water('wateredYesterday', wateredYesterday);
  }, [wateredYesterday]);

  const eventId = _eventId ?? waterEventId;

  const { event } = useEvent({ id: eventId ?? '' });

  useEffect(() => {
    if (error) {
      setIsWatering(false);
      toast.error(`Error watering: ${error}`, { id: error });
      console.error('useSquashWater useRequestCreateEvent', error);
    }
  }, [error]);

  useEffect(() => {
    if (!settlementStatus) return;
    if (
      (settlementStatus === 'SettledWithRecords' ||
        settlementStatus === 'Settled') &&
      event?.functionId === 'water_and_level_up' &&
      prevLevelRef.current === 4
    ) {
      setGameOver?.(true);
    } else if (['SettledWithRecords', 'Failed'].includes(settlementStatus)) {
      setIsWatering(false);
      log_water('clearing the url query params');
      navigate({
        to: GameIndexRoute.to,
        search: {
          finishingGame:
            functionId === 'water_and_level_up' &&
            (activeSquash?.onChainLevel === 4 || prevLevelRef.current === 4),
        },
      });
      if (settlementStatus === 'Failed') {
        console.error(
          `Water event failed: ${event?.error} with inputs`,
          event?.inputs
        );
      }
    } else if (['Creating', 'Pending'].includes(settlementStatus)) {
      setIsWatering(true);
      navigate({
        to: GameIndexRoute.to,
        search: {
          expectedRecordCount: expectedRecordCount,
          waterEventId: eventId,
          finishingGame:
            functionId === 'water_and_level_up' &&
            (activeSquash?.onChainLevel === 4 || prevLevelRef.current === 4),
        },
      });
    }
  }, [
    settlementStatus,
    activeSquashRecords?.length,
    eventId,
    prevLevelRef.current,
  ]);

  const water = useCallback(() => {
    if (readyToWater && needsWater) {
      try {
        createEvent();
      } catch (e) {
        console.error('Error creating water event', e);
        throw e;
      }
      setIsWatering(true);
      setExpectedRecordCount(activeSquashRecords.length + 1);
      navigate({
        to: GameIndexRoute.to,
        search: { expectedRecordCount: activeSquashRecords.length + 1 },
      });
    } else {
      console.error(
        'activeSquashRecord.plaintext or dayStartUnix is undefined',
        activeSquashRecord,
        dayStartUnix,
        activeSquashRecords
      );
    }
  }, [readyToWater, needsWater, activeSquashRecords?.length]);

  return {
    gameOver,
    error,
    eventId,
    loading,
    readyToLevelUp,
    readyToWater,
    needsWater,
    isWatering,
    water,
    prevLevelRef,
  };
};

export const useSquashMint = (
  name: string,
  parcRecord?: RecordWithPlaintext
) => {
  const {
    expectedRecordCount: _expectedRecordCount,
    mintEventId: mintEventId_a,
  } = useSearch({
    from: MintRoute.id,
  });

  const { events } = useEvents({
    network,
    filter: {
      programId,
      functionId: 'mint',
    },
  });
  const mintEventId_b = useMemo(() => {
    const event = events?.find((e) =>
      [EventStatus.Creating, EventStatus.Pending].includes(e.status)
    );
    return event?._id;
  }, [JSON.stringify(events)]);

  const mintEventId = mintEventId_a ?? mintEventId_b;

  const { account } = useSquashGameState();
  const { activeSquashRecord, activeSquashRecords } = useSquash();
  const currentRecordCount = activeSquashRecords?.length;

  const [expectedRecordCount, setExpectedRecordCount] =
    useState(_expectedRecordCount);

  useEffect(() => {
    if (currentRecordCount !== undefined && expectedRecordCount === undefined) {
      setExpectedRecordCount(currentRecordCount + 1);
    }
  }, [currentRecordCount, expectedRecordCount]);

  const navigate = useNavigate({ from: MintRoute.to });

  const { dayStartUnix, endTimeUnix, gameIsActive } = useSquashTime();
  const [isMinting, setIsMinting] = useState(false);

  const feeQuery = useFeeQuery({
    programId,
    functionId: 'mint',
    network,
  });
  const fee = feeQuery.data?.fee?.fee.total;

  const readyToMint =
    name.length > 0 &&
    name.length <= 16 &&
    !!account &&
    !!dayStartUnix &&
    !!activeSquashRecords &&
    !!endTimeUnix &&
    !!fee &&
    !!parcRecord?.plaintext &&
    endTimeUnix > Date.now() / 1000 &&
    gameIsActive;

  useEffect(() => {
    log_mint({
      name,
      account,
      dayStartUnix,
      activeSquashRecords,
      endTimeUnix,
      gameIsActive,
      parcRecord,
    });
  }, [
    name,
    JSON.stringify(account),
    JSON.stringify(dayStartUnix),
    JSON.stringify(activeSquashRecords),
    endTimeUnix,
    gameIsActive,
    parcRecord,
  ]);

  const name_u128 = asciiToU128(name);

  const { event } = useEvent({ id: mintEventId ?? '' });

  const cachedName_u128 = event?.inputs[2];
  const cachedName = cachedName_u128 ? u128ToAscii(cachedName_u128) : undefined;

  const {
    createEvent,
    error,
    eventId: _eventId,
    loading,
    settlementStatus,
  } = useRequestCreateEvent({
    programId,
    fee: fee!,
    functionId: 'mint',
    inputs: readyToMint
      ? [
          parcRecord.plaintext,
          account.address,
          name_u128,
          `${getRandomU32()}u32`,
          `${dayStartUnix}u64`,
        ]
      : [],
    type: EventType.SquashMint,
    settlementInfo:
      currentRecordCount !== undefined && expectedRecordCount !== undefined
        ? {
            currentRecordCount,
            expectedRecordCount,
            eventId: mintEventId,
          }
        : undefined,
    tokenIds: ['3126633106field'],
  });

  const eventId = _eventId ?? mintEventId;

  useEffect(() => {
    mintEventId_a && log_mint('mintEventId_a', mintEventId_a);
  }, [mintEventId_a]);

  useEffect(() => {
    mintEventId_b && log_mint('mintEventId_b', mintEventId_b);
  }, [mintEventId_b]);

  useEffect(() => {
    settlementStatus && log_mint('settlementStatus', settlementStatus);
  }, [settlementStatus]);

  useEffect(() => {
    currentRecordCount && log_mint('currentRecordCount', currentRecordCount);
  }, [currentRecordCount]);

  useEffect(() => {
    _expectedRecordCount &&
      log_mint('_expectedRecordCount', _expectedRecordCount);
  }, [_expectedRecordCount]);

  useEffect(() => {
    expectedRecordCount && log_mint('expectedRecordCount', expectedRecordCount);
  }, [expectedRecordCount]);

  useEffect(() => {
    if (error) {
      setIsMinting(false);
      toast.error(`Error minting: ${error}`, { id: error });
      console.error('useSquashMint useRequestCreateEvent', error);
    }
  }, [error]);

  useEffect(() => {
    if (!settlementStatus) return;
    if (['SettledWithRecords', 'Failed'].includes(settlementStatus)) {
      setIsMinting(false);
      log_mint('clearing the url query params');
      navigate({
        to: MintRoute.to,
      });
      if (settlementStatus === 'Failed') {
        console.error(
          `Mint event failed: ${event?.error} with inputs`,
          event?.inputs
        );
      }
    } else if (['Creating', 'Pending'].includes(settlementStatus)) {
      setIsMinting(true);
      navigate({
        to: MintRoute.to,
        search: {
          expectedRecordCount: expectedRecordCount,
          mintEventId: eventId,
        },
      });
    }
  }, [settlementStatus, activeSquashRecords?.length, eventId, error]);

  const mint = useCallback(() => {
    if (readyToMint) {
      try {
        createEvent();
      } catch (e) {
        console.error('Error creating mint event', e);
        throw e;
      }
      setIsMinting(true);
      setExpectedRecordCount(activeSquashRecords.length + 1);
      navigate({
        to: MintRoute.to,
        search: { expectedRecordCount: activeSquashRecords.length + 1 },
      });
    } else {
      console.error(
        'squashRecord.plaintext or dayStartUnix is undefined',
        activeSquashRecord,
        dayStartUnix,
        activeSquashRecords
      );
    }
  }, [readyToMint, name, activeSquashRecords?.length]);

  return {
    error,
    eventId,
    loading,
    readyToMint,
    isMinting,
    cachedName,
    mint,
  };
};

export const useSquashTime = () => {
  const { data } = useQuery({
    queryKey: [programId, network],
    queryFn: async () => {
      const nodeUrl = import.meta.env.VITE_ALEO_NETWORK_URL!;
      const networkStr =
        network === Network.AleoMainnet ? 'mainnet' : 'testnet';
      const url = `${nodeUrl}/${networkStr}/program/${programId}/mapping/day_start/0u8`;
      const response = await fetch(url);
      if (response.ok) {
        const text = await response.text();
        if (text === 'null') {
          return 0;
        }
        return extractAmountFromInputString(text.replace(/"/g, '').trim()) ?? 0;
      } else {
        // Try to extract error message from the response
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          // Try to parse as JSON first
          const errorData = await response.json();
          errorMessage = errorData.message || JSON.stringify(errorData);
        } catch {
          // If not JSON, try as text
          try {
            errorMessage = await response.text();
          } catch {
            // fallback to status text
            errorMessage = response.statusText;
          }
        }
        throw new Error(errorMessage);
      }
    },
    refetchInterval: 10_000,
    staleTime: 10_000,
  });

  const BUFFER_TIME_S = 60;

  const timeDeltaSeconds = network === Network.AleoMainnet ? 86_400 : 600;
  const endTimeUnix = data ? data + timeDeltaSeconds : undefined;
  const now = Date.now() / 1000;
  const isEndOfDay = endTimeUnix
    ? endTimeUnix - now < BUFFER_TIME_S
    : undefined;
  const gameIsActive = !!data && !isEndOfDay;

  return {
    dayStartUnix: data,
    now,
    gameIsActive,
    endTimeUnix,
    timeDeltaSeconds,
  };
};
